import get from 'lodash-es/get'
import { Site } from '@envato/foundation-types'

import { getCurrentSiteDomain } from './sites'
import {
  reportProductClick,
  reportProductAddToCartAttempt,
  reportModalPageView,
  reportAudioPreviewDownload,
  reportVideoPreviewDownload
} from '../analytics/resultAnalytics'
import { logError } from './errors'
import { getTopLevelRoute } from './urlHelper'
import { getConfigBySite } from '../../config/sites'

export const formatItemNameForPreview = item => {
  const id = get(item, 'id', '')
  const name = get(item, 'name', '').replace(/\W/g, '-')
  const author = get(item, 'author_username', '').replace(/\W/g, '-')

  return `${id}_${name}_by_${author}_preview.mp3`.toLowerCase()
}

export const isSearchResultMatchesEqual = (mainMatches, comparingMatches) => {
  const hasSameLength = mainMatches.length === comparingMatches.length
  if (!hasSameLength) {
    return false
  }
  const comparisonResults = mainMatches
    .map((item, index) => {
      return item && comparingMatches && item.id === comparingMatches[index].id
    })
    .every(isIdEqual => isIdEqual)

  return comparisonResults
}

// https://github.com/envato/foundation-docs/blob/master/docs/shopfront_special_ui_rules.md
export const filterSortByOptions = (currentSite, parsedLocation, options) => {
  let filteredOptions = [...options]
  // no ratings for photodune
  if (currentSite === Site.photodune) {
    filteredOptions = filteredOptions.filter(options => {
      return options.value !== 'rating'
    })
  }

  // no `Best Match` if there's no search term
  if (!parsedLocation.term) {
    filteredOptions = filteredOptions.filter(options => {
      return options.value !== 'relevance'
    })
  }
  return filteredOptions
}

export const getItemAttribute = (item, name) => {
  return get(
    item.attributes.find(attribute => attribute.name === name),
    'value'
  )
}

export const getItemRankOffset = (currentSite, currentPage) => {
  const pageSize = getConfigBySite(currentSite, 'common.search_page_size')
  return ((currentPage || 1) - 1) * pageSize + 1
}

export const itemDecorator = (
  currentSite,
  openModal,
  signedOut,
  favoriteCollectionID,
  collections,
  deleteBookmark,
  addBookmark,
  createFavoriteCollection,
  itemRankOffset,
  cartItems,
  location,
  listName
) => {
  return (item, index) => {
    const { isInFavourites } = item.ui

    const rank = index + itemRankOffset
    const topLevelRoute = getTopLevelRoute(location.pathname)
    const defaultListName =
      topLevelRoute === 'category'
        ? 'Search Results: Category'
        : 'Search Results: Search'
    const reportProductClickHandler = reportProductClick.bind(null, {
      index: rank,
      item,
      currentSite,
      listName: listName || defaultListName
    })
    const reportProductAddToCartAttemptHandler = reportProductAddToCartAttempt.bind(
      null,
      {
        item,
        index: rank,
        currentSite,
        pageType: topLevelRoute
      }
    )

    const onAddToFavourites = event => {
      event.preventDefault()
      if (signedOut) {
        window.location = `//${getCurrentSiteDomain(
          currentSite
        )}/sign_in?action_name=favorite&resource_id=${item.id}&return_to=${
          window.location.pathname
        }${window.location.search}`
      } else {
        // check whether user has favorite collection
        if (favoriteCollectionID) {
          if (isInFavourites) {
            const bookmark =
              collections[favoriteCollectionID].bookmarks[item.id]
            if (bookmark.id && bookmark.optimisticStatus !== 'pending') {
              const isFavouriteCollection = true
              deleteBookmark(
                favoriteCollectionID,
                item.id,
                bookmark.id,
                isFavouriteCollection
              )
            }
          } else {
            const isFavouriteCollection = true
            addBookmark(favoriteCollectionID, item.id, isFavouriteCollection)
          }
        } else {
          createFavoriteCollection()
            .then(action => {
              if (action && action.type === 'createCollectionSuccess') {
                const newCollectionId = action.payload.id
                // this is used for ga tracking so we know it's add to favourite collection
                const isFavouriteCollection = true
                return addBookmark(
                  newCollectionId,
                  item.id,
                  isFavouriteCollection
                )
              }
            })
            .catch(logError)
        }
      }
    }

    const onAddToCollections = event => {
      event.preventDefault()
      if (signedOut) {
        window.location = `//${getCurrentSiteDomain(
          currentSite
        )}/sign_in?action_name=collection&resource_id=${item.id}&return_to=${
          window.location.pathname
        }${window.location.search}`
      } else {
        reportModalPageView({
          title: 'Modal: Add item to collection',
          page: `${window.location.pathname}/add-item-to-collection${
            window.location.search
          }`
        })
        openModal({
          type: 'addItemToCollection',
          payload: {
            item
          }
        })
      }
    }

    const onAddToCart = event => {
      event.preventDefault()
      reportProductAddToCartAttemptHandler()

      reportModalPageView({
        title: 'Modal: Customize item before adding to cart',
        page: `${
          window.location.pathname
        }/customize-item-before-adding-to-cart${window.location.search}`
      })
      openModal({
        type: 'addItemToCart',
        payload: {
          item
        }
      })
    }

    const onDownloadAudioPreview = async event => {
      reportAudioPreviewDownload(item.id)
    }

    const onDownloadVideoPreview = async event => {
      reportVideoPreviewDownload(item.id)
    }

    return {
      item,
      rank,
      onItemLinkClick: reportProductClickHandler,
      onAddToCart,
      onAddToFavourites,
      onAddToCollections,
      onDownloadAudioPreview,
      onDownloadVideoPreview
    }
  }
}
